/**
 * Character type definitions for Idle Depths.
 *
 * Characters are the primary game entities that hold stats, progress,
 * and are persisted to localStorage with server backup.
 */

import type { CharacterEquipment } from './item.js';
import type { TravelActivityData } from './location.js';
import type { SkillProgress, SkillingActivityData } from './skill.js';
import type { CharacterStat } from './stats.js';

// ============================================================================
// Activity Types (Discriminated Union)
// ============================================================================

/**
 * Base activity interface shared by all activity types.
 */
interface BaseActivity {
  /** When the activity started (Unix timestamp ms) */
  startedAt: number;

  /** Progress tracking for current action (0-1) */
  progress: number;
}

/**
 * Idle activity - character is not doing anything.
 */
export interface IdleActivity extends BaseActivity {
  /** Activity type discriminator */
  type: 'idle';
}

/**
 * Skilling activity - character is performing a skill action.
 */
export interface SkillingActivity extends BaseActivity {
  /** Activity type discriminator */
  type: 'skilling';

  /** Skill-specific activity data */
  activityData: SkillingActivityData;
}

/**
 * Combat activity - character is in combat (future).
 */
export interface CombatActivity extends BaseActivity {
  /** Activity type discriminator */
  type: 'combat';

  /** Combat-specific activity data */
  activityData: Record<string, unknown>;
}

/**
 * Traveling activity - character is traveling between locations.
 */
export interface TravelingActivity extends BaseActivity {
  /** Activity type discriminator */
  type: 'traveling';

  /** Travel-specific activity data */
  activityData: TravelActivityData;
}

/**
 * Current activity discriminated union.
 *
 * Use type narrowing with `activity.type` to access specific activity data.
 */
export type CurrentActivity = IdleActivity | SkillingActivity | CombatActivity | TravelingActivity;

// ============================================================================
// Activity Factory Functions
// ============================================================================

/**
 * Create an idle activity.
 *
 * @returns New idle activity
 */
export function createIdleActivity(): IdleActivity {
  return {
    type: 'idle',
    startedAt: Date.now(),
    progress: 0,
  };
}

/**
 * Create a traveling activity.
 *
 * @param travelData - Travel-specific data including route and timing
 * @returns New traveling activity
 */
export function createTravelingActivity(travelData: TravelActivityData): TravelingActivity {
  return {
    type: 'traveling',
    startedAt: travelData.startedAt,
    progress: 0,
    activityData: travelData,
  };
}

/**
 * Create a skilling activity.
 *
 * @param activityData - Skilling activity data
 * @returns New skilling activity
 */
export function createSkillingActivity(activityData: SkillingActivityData): SkillingActivity {
  return {
    type: 'skilling',
    startedAt: activityData.startedAt,
    progress: 0,
    activityData,
  };
}

/**
 * Complete character data stored in localStorage and synced to server.
 *
 * Characters are the primary game entity containing all player progress.
 * Data is stored locally first, then backed up to the server.
 */
export interface CharacterData {
  /** Unique character identifier (nanoid) */
  id: string;

  /** Character name (player-chosen) */
  name: string;

  /** Slot index for ordering (0-based) */
  slotIndex: number;

  /** When the character was created (Unix timestamp ms) */
  createdAt: number;

  /** When the character was last modified (Unix timestamp ms) */
  updatedAt: number;

  /** When the character was last played (Unix timestamp ms) */
  lastPlayedAt: number;

  /** Total play time in seconds */
  totalPlayTime: number;

  /** Current location identifier */
  currentLocationId: string;

  /** Dynamic stats (health, etc.) */
  stats: Record<string, CharacterStat>;

  /** Current activity (what the character is doing) */
  currentActivity: CurrentActivity;

  /** Skill progress (XP, levels, affinity) - added in v0.2.5 */
  skills: Record<string, SkillProgress>;

  /** Currency (gold) - added in v0.2.3 */
  currency: number;

  /** Equipped items by slot - added in v0.2.3 */
  equipment: CharacterEquipment;

  // Sync metadata

  /** When last synced to server (Unix timestamp ms, null if never synced) */
  lastSyncedAt: number | null;

  /** Whether this character has changes pending sync to server */
  needsSync: boolean;
}

/**
 * Summary data for character selection screen (lightweight).
 */
export interface CharacterSummary {
  /** Unique character identifier */
  id: string;

  /** Character name */
  name: string;

  /** Slot index for ordering */
  slotIndex: number;

  /** When the character was last played */
  lastPlayedAt: number;

  /** Total play time in seconds */
  totalPlayTime: number;

  /** Whether this character has changes pending sync */
  needsSync: boolean;
}

/**
 * Export format for character data.
 *
 * Includes version for future schema migrations.
 */
export interface CharacterExport {
  /** Export format version */
  version: 1;

  /** When the export was created (Unix timestamp ms) */
  exportedAt: number;

  /** The character data */
  character: CharacterData;
}

/**
 * Maximum characters per user.
 */
export const MAX_CHARACTERS_PER_USER = 5;

/**
 * Maximum character name length.
 */
export const MAX_CHARACTER_NAME_LENGTH = 20;

/**
 * Minimum character name length.
 */
export const MIN_CHARACTER_NAME_LENGTH = 3;

/**
 * Default starting location for new characters.
 */
export const DEFAULT_STARTING_LOCATION = 'coastal_village';

/**
 * Current character export format version.
 */
export const CHARACTER_EXPORT_VERSION = 1;
